import { ReactElement, ReactNode } from "react"
import { useParams } from "react-router-dom"

import CreateDeviceProvider from "./baseProviders/createDeviceProvider"
import useGetTTRDeviceFormDataFromApiResponse from "../../../../hooks/useGetTTRDeviceFormDataFromApiResponse"
import useContinueDeviceDraft from "../../../../services/middleware/hooks/devices/ttr/useContinueDeviceDraft"
import { TTypeTestVersionPostBody } from "../../../../services/middleware/types/devices/ttr/publishDeviceTypes"

interface IContinueVersionUrlParams {
    deviceRef: string
}
interface IContinueVersionProviderProps {
    children: ReactNode
}

const ContinueVersionProvider = ({
    children,
}: IContinueVersionProviderProps): ReactElement => {
    const { deviceRef: urlDeviceRef } = useParams<
        keyof IContinueVersionUrlParams
    >() as IContinueVersionUrlParams

    const {
        data,
        isLoading,
        isValidating,
        error: continueDraftError,
    } = useContinueDeviceDraft(urlDeviceRef)

    const { formData, setFormData } = useGetTTRDeviceFormDataFromApiResponse(
        isLoading,
        isValidating,
        continueDraftError,
        data
    )

    const deviceId = data?.DeviceID ?? undefined
    const deviceRef = data?.DeviceRef ?? undefined

    const attachmentUUID = data?.Attachment_UUID ?? undefined
    const supersededDeviceId = data?.SupersededDeviceID ?? undefined
    const complianceComments = data?.ComplianceComments ?? undefined
    const complianceStatusNotes = data?.ComplianceStatusNotes ?? undefined

    const typeTestVersionPostBody: TTypeTestVersionPostBody | undefined = data
        ? {
              G98GBVersion: data.G98GBVersion ?? undefined,
              G98NIVersion: data.G98NIVersion ?? undefined,
              G99GBVersion: data.G99GBVersion ?? undefined,
              G99NIVersion: data.G99NIVersion ?? undefined,
          }
        : undefined

    return (
        <CreateDeviceProvider
            formData={formData}
            setFormData={setFormData}
            formDataIsLoading={isLoading}
            formDataError={continueDraftError}
            deviceId={deviceId}
            deviceRef={deviceRef}
            canEditManufacturer={false}
            attachmentUUID={attachmentUUID}
            supersededDeviceID={supersededDeviceId}
            complianceComments={complianceComments}
            complianceStatusNotes={complianceStatusNotes}
            typeTestVersionPostBody={typeTestVersionPostBody}
        >
            {children}
        </CreateDeviceProvider>
    )
}

export default ContinueVersionProvider
